import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle } from "@hp/ui";
import type { AlertSettings, DigestFrequency, UrgentChannel } from "@hp/api-client";
import { updateAlertSettings } from "../api/alertSettings";
import { ErrorBlock } from "./QueryStatus";
import { formatDate } from "../lib/format";

const URGENT_CHANNELS: UrgentChannel[] = ["email", "whatsapp", "slack"];
const URGENT_CHANNEL_LABEL: Record<UrgentChannel, string> = { email: "Email", whatsapp: "WhatsApp", slack: "Slack" };
const DIGEST_FREQUENCIES: DigestFrequency[] = ["daily", "weekly"];
const DIGEST_FREQUENCY_LABEL: Record<DigestFrequency, string> = { daily: "Daily", weekly: "Weekly" };

export function AlertSettingsForm({ settings }: { settings: AlertSettings }) {
  const queryClient = useQueryClient();
  const [threshold, setThreshold] = React.useState(settings.threshold_cr);
  const [urgentChannel, setUrgentChannel] = React.useState(settings.urgent_channel);
  const [digestFrequency, setDigestFrequency] = React.useState(settings.digest_frequency);

  React.useEffect(() => {
    setThreshold(settings.threshold_cr);
    setUrgentChannel(settings.urgent_channel);
    setDigestFrequency(settings.digest_frequency);
  }, [settings]);

  const mutation = useMutation({
    mutationFn: updateAlertSettings,
    onSuccess: (updated) => {
      queryClient.setQueryData(["alert-settings"], updated);
    },
  });

  function handleSave(event: React.FormEvent) {
    event.preventDefault();
    mutation.mutate({
      threshold_cr: threshold,
      urgent_channel: urgentChannel,
      digest_frequency: digestFrequency,
    });
  }

  return (
    <form onSubmit={handleSave}>
      <Card>
        <CardHeader>
          <CardTitle>When should we interrupt the team?</CardTitle>
        </CardHeader>
        <CardBody className="space-y-6">
          <div>
            <label htmlFor="settings-threshold" className="block text-sm font-medium text-zinc-700">
              Notify immediately when a claim is worth more than
            </label>
            <div className="mt-2 flex items-center gap-3">
              <span className="text-sm text-zinc-500">₹</span>
              <input
                id="settings-threshold"
                type="range"
                min={5}
                max={500}
                step={5}
                value={threshold}
                onChange={(event) => setThreshold(Number(event.target.value))}
                className="h-1.5 w-64 max-w-full accent-brand-600"
              />
              <span className="min-w-[4.5rem] font-mono text-sm font-semibold tabular-nums text-zinc-900">
                {threshold} Cr
              </span>
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">
              Anything below this amount still shows up in the regular digest, not as an urgent alert.
            </p>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-700">Send urgent alerts by</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {URGENT_CHANNELS.map((channel) => (
                <button
                  type="button"
                  key={channel}
                  onClick={() => setUrgentChannel(channel)}
                  className={[
                    "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                    urgentChannel === channel
                      ? "border-brand-600 bg-brand-50 text-brand-700"
                      : "border-zinc-300 text-zinc-600 hover:bg-zinc-50",
                  ].join(" ")}
                >
                  {URGENT_CHANNEL_LABEL[channel]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-700">Send the regular digest</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {DIGEST_FREQUENCIES.map((frequency) => (
                <button
                  type="button"
                  key={frequency}
                  onClick={() => setDigestFrequency(frequency)}
                  className={[
                    "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                    digestFrequency === frequency
                      ? "border-brand-600 bg-brand-50 text-brand-700"
                      : "border-zinc-300 text-zinc-600 hover:bg-zinc-50",
                  ].join(" ")}
                >
                  {DIGEST_FREQUENCY_LABEL[frequency]}
                </button>
              ))}
            </div>
          </div>
        </CardBody>
        <CardFooter className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save preferences"}
          </Button>
          <span className="text-xs text-zinc-500">
            {settings.updated_by_email
              ? `Last changed by ${settings.updated_by_email} on ${formatDate(settings.updated_at)}`
              : "Never changed — showing defaults"}
          </span>
        </CardFooter>
        {mutation.isError ? (
          <div className="border-t border-zinc-100 px-6 py-3">
            <ErrorBlock
              message={mutation.error instanceof Error ? mutation.error.message : "Could not save alert settings."}
              surface="canvas"
            />
          </div>
        ) : null}
        {mutation.isSuccess ? (
          <div className="border-t border-emerald-100 bg-emerald-50 px-6 py-2 text-xs text-emerald-800">
            Preferences saved. Urgent alerts use ₹{threshold} Cr and digest is {DIGEST_FREQUENCY_LABEL[digestFrequency].toLowerCase()}.
          </div>
        ) : null}
      </Card>
    </form>
  );
}
